import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import SeoHead from "../components/SeoHead";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function renderContent(content) {
  return content.split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) {
      return <h2 key={i} className="font-serif text-3xl text-ink mt-12 mb-4">{block.slice(3)}</h2>;
    }
    return <p key={i} className="text-charcoal/80 text-lg leading-[1.8] mb-6">{block}</p>;
  });
}

export default function InsightDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/posts/${slug}`)
      .then((r) => { setPost(r.data.post); setRelated(r.data.related); })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-[72px] min-h-[70vh] flex flex-col items-center justify-center gap-6">
        <h1 className="font-serif text-4xl text-ink">Article not found</h1>
        <Link to="/insights" className="text-steel hover:text-ink">← Back to Insights</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="pt-[72px] min-h-[70vh] flex items-center justify-center text-steel">Loading…</div>;
  }

  return (
    <div data-testid="insight-detail-page" className="pt-[72px]">
      <SeoHead
        title={(post.seo_title && post.seo_title.trim()) || `${post.title} | Local Proof STL Insights`}
        description={(() => {
          const raw = (post.seo_description && post.seo_description.trim()) || (post.excerpt || "").trim();
          return raw.length > 158 ? raw.slice(0, 155).trimEnd() + "…" : raw;
        })()}
        canonical={`https://localproofstl.com/insights/${post.slug}`}
        ogType="article"
        ogUrl={`https://localproofstl.com/insights/${post.slug}`}
        ogImage={post.cover_image || "https://localproofstl.com/og-image.jpg"}
        twitterImage={post.cover_image || "https://localproofstl.com/og-image.jpg"}
        jsonLdId="article-jsonld"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt || "",
          "image": post.cover_image ? [post.cover_image] : undefined,
          "datePublished": post.published_at,
          "dateModified": post.updated_at || post.published_at,
          "author": { "@type": "Person", "name": post.author || "Local Proof STL" },
          "publisher": { "@type": "Organization", "name": "Local Proof STL" },
          "mainEntityOfPage": `https://localproofstl.com/insights/${post.slug}`,
          "articleSection": post.category,
          "keywords": (post.tags || []).join(", "),
        }}
      />
      <article className="max-w-[760px] mx-auto px-6 pt-16 lg:pt-24 pb-16">
        <Reveal>
          <button onClick={() => navigate("/insights")} className="inline-flex items-center gap-2 text-steel hover:text-ink text-sm mb-10 transition-colors">
            <ArrowLeft size={15} /> Insights
          </button>
          <div className="flex items-center gap-3 text-xs text-steel mb-6">
            <span className="uppercase tracking-widest font-semibold">{post.category}</span>
            <span>·</span><span>{fmtDate(post.published_at)}</span>
            <span>·</span><span>{post.reading_time} min read</span>
          </div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink mb-6">
            {post.title}
          </h1>
          <p className="text-xl text-charcoal/70 leading-relaxed mb-8">{post.excerpt}</p>
          <div className="flex items-center gap-3 pb-8 border-b border-border">
            <span className="w-9 h-9 rounded-full bg-ink text-white flex items-center justify-center font-serif text-sm">L</span>
            <span className="text-sm font-medium text-charcoal">{post.author}</span>
          </div>
        </Reveal>
      </article>

      <div className="max-w-[760px] mx-auto px-6">
        <img src={post.cover_image} alt={post.title} className="w-full rounded-2xl border border-border mb-12" />
      </div>

      <div className="max-w-[760px] mx-auto px-6 pb-16">{renderContent(post.content)}</div>

      {post.tags?.length > 0 && (
        <div className="max-w-[760px] mx-auto px-6 pb-16 flex flex-wrap gap-2">
          {post.tags.map((t) => (
            <span key={t} className="text-xs text-steel bg-surface border border-border rounded-full px-3 py-1.5">#{t}</span>
          ))}
        </div>
      )}

      {related.length > 0 && (
        <section className="bg-surface border-t border-border py-20">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <p className="eyebrow mb-10">Related reading</p>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link key={r.id} to={`/insights/${r.slug}`} className="group block" data-testid={`related-${r.slug}`}>
                  <div className="relative h-44 rounded-xl overflow-hidden mb-4 border border-border">
                    <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <span className="text-xs uppercase tracking-widest font-semibold text-steel">{r.category}</span>
                  <h3 className="font-serif text-xl text-ink mt-2 group-hover:text-steel transition-colors">{r.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 text-center">
        <Link to="/contact" data-testid="article-book-cta" data-track="request-assessment-article" className="group inline-flex items-center gap-2 bg-ink text-white px-7 py-4 rounded-full text-sm font-semibold hover:bg-charcoal transition-colors">
          Request an Assessment <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </section>
    </div>
  );
}
